import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, EMPTY, Observable, throwError } from 'rxjs';
import { catchError, filter, retry, switchMap, take, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../services/toast.service';
import { APP_URL } from '../shared/constants';

@Injectable()
export class HttpConfigInterceptor implements HttpInterceptor {
  // Only 1 refreshToken can be called at a time
  private isRefreshingToken = false;
  private refreshTokenQueue: BehaviorSubject<any>[] = [];

  constructor(
    private router: Router, 
    private toastService: ToastService,
    private authService: AuthService,
    private translateService: TranslateService) { }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    let updatedRequest = request;
    const token = this.authService.getAccessToken();
    updatedRequest = this.addTokenHeader(updatedRequest, token);
    updatedRequest = this.addDefaultHeaders(updatedRequest);

    return next.handle(updatedRequest).pipe(
      retry(0),
      catchError((error: HttpErrorResponse) => {
        return this.statusHandler(error, updatedRequest, next);
      })
    );
  }

  private handleAuthError(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    const refreshTokenSubject = new BehaviorSubject<any>(null);
    this.refreshTokenQueue.push(refreshTokenSubject);
    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true;
      const accessToken = this.authService.getAccessToken();
      const refreshToken = this.authService.getRefreshToken();
      this.authService
        .refreshToken({ accessToken, refreshToken })
        .pipe(
          tap((response: any) => {
            // Refresh token successful: Update local storage and notify queued requests
            this.isRefreshingToken = false;
            this.authService.setLocalStorage(response.token);
            const newAccessToken = this.authService.getAccessToken();
            // Notify queued requests with the new token
            this.refreshTokenQueue.forEach((queueSubject) => queueSubject.next(newAccessToken));
            this.refreshTokenQueue = [];
          }),
          catchError((err) => {
            // Handle token refresh errors
            this.isRefreshingToken = false;
            if (err.error === 'Invalid Token') {
              this.authService.signOut();
              this.router.navigateByUrl(APP_URL.LOGIN);
            }
            return EMPTY;
          })
        )
        .subscribe(); // Manual subscription to handle side effects
    }
    // Call request by queueSubject
    return refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      // Recall request after have new token
      switchMap((token) => next.handle(this.addTokenHeader(request, token))),
      catchError((err) => {
        // Only throw error retrying fails
        // to make Fe still got data with 401
        return throwError(() => err);
      })
    );
  }

  private addTokenHeader(req: HttpRequest<any>, token: string | null) {
    if (token) {
      const headers = req.headers.set('Authorization', `Bearer ${token}`);
      return req.clone({ headers });
    }
    return req;
  }

  addDefaultHeaders(req: HttpRequest<any>) {
    const headers = req.headers;
    return req.clone({ headers });
  }

  private statusHandler(error: HttpErrorResponse, request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    const currentUrl = this.router.url;
    switch (error.status) {
      case 401:
        return this.handleAuthError(request, next);
      case 403:
        currentUrl !== APP_URL.LOGIN && this.router.navigateByUrl(APP_URL.LOGIN);
        break;
      case 404:
        this.toastService.error(this.translateService.instant('Error 404'));
        break;
      case 429:
        this.toastService.warning(this.translateService.instant('too many requests'));
        break;
      case 500:
        this.router.navigateByUrl(APP_URL.ERROR_500);
        break;
      default:
        if (error.error) {
          // refreshToken return 400
          if (error.error === 'Invalid Token') {
            return throwError(() => error);
          }
          return throwError(() => error.error);
        }
    }

    return throwError(() => new Error(error.error));
  }
}
